import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights, DistributedTracingModes } from '@microsoft/applicationinsights-web';
import { useVariable } from '@softwareimaging/backstage';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createStore, StoreApi, useStore } from 'zustand';
import { Variables } from '../backstage/config';

import now from '~build/time';
import { mapLogLevelToAppInsights, useDebug, useLogger } from './DebugProvider';

declare global {
  interface Window {
    appInsights: ApplicationInsights;
  }
}

interface AppInsightsStore {
  userId?: string;
  context?: Record<string, any>;
  getSessionId: () => string | undefined;
  trackException: (exception: Error) => void;
  addAuthenticatedUserContext: (userId: string) => void;
  addCustomContext: (context: Record<string, any>) => void;
  trackEvent: (event: any, properties?: { [key: string]: string }) => void;
}
const AppInsightsContext = createContext<StoreApi<AppInsightsStore> | null>(null);

export const AppInsightsProvider: FC<PropsWithChildren> = ({ children }) => {
  const connectionString = useVariable<Variables>('appInsightsConnectionString');
  if (!connectionString) throw new Error('AppInsights connection string is not set');

  const log = useDebug('AppInsightsProvider');
  const { logger, level } = useLogger();

  const [appInsights] = useState<ApplicationInsights>(() => {
    const reactPlugin = new ReactPlugin();
    const insights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        extensions: [reactPlugin],
        // Without GDPR consent, we can't store any data related to analytics
        isStorageUseDisabled: true,

        // We can able end-to-end correlation with the backend with the following settings
        enableCorsCorrelation: true,
        distributedTracingMode: DistributedTracingModes.W3C,

        // We can add additional context to the request
        addRequestContext: () => ({
          // We can also add the build information to the request
          builtAt: now?.toISOString()
        })
      }
    });
    return insights;
  });

  const store = useMemo(
    () =>
      createStore<AppInsightsStore>((set, get) => ({
        getSessionId: () => {
          if (!appInsights) {
            return 'No session in development';
          }
          return appInsights?.context?.getSessionId();
        },
        trackException: (exception: Error) => {
          if (!appInsights.appInsights.isInitialized()) {
            log.error(exception);
            return;
          }
          const { userId } = get();
          if (userId) {
            appInsights.setAuthenticatedUserContext(userId);
          }

          appInsights.trackException({ exception });
        },
        addCustomContext: (context: Record<string, any>) => {
          set({ context });

          if (!appInsights.appInsights.isInitialized()) {
            log.warn(`AppInsights is not enabled in development or hasn't been initialized`);
            return;
          }

          appInsights.addTelemetryInitializer(envelope => {
            if (!envelope.data) envelope.data = {};
            for (const [name, value] of Object.entries(context)) {
              envelope.data[name] = value;
            }
          });
        },
        addAuthenticatedUserContext: (userId: string) => {
          set({ userId });

          if (!appInsights.appInsights.isInitialized()) {
            log.warn(`AppInsights is not enabled in development or hasn't been initialized`);
            return;
          }
          appInsights.setAuthenticatedUserContext(userId);
        },
        trackEvent: (event: string, properties?: { [key: string]: string }) => {
          if (!appInsights.appInsights.isInitialized()) {
            log.warn(`AppInsights is not enabled in development: ${event}`);
            return;
          }
          const { userId } = get();
          if (userId) {
            appInsights.setAuthenticatedUserContext(userId);
          }
          appInsights.trackEvent({ name: event }, properties);
        }
      })),
    [appInsights, log]
  );

  useEffect(() => {
    // if (import.meta.env.DEV) {
    //   console.warn('AppInsights is not enabled in development');
    //   return;
    // }

    if (appInsights.appInsights.isInitialized()) {
      return;
    }

    appInsights.loadAppInsights();
    const { userId, context } = store.getState();
    if (userId) {
      log.info(`Setting authenticated user context to ${userId} in initialization`);
      appInsights.setAuthenticatedUserContext(userId);
    }

    if (context) {
      appInsights.addTelemetryInitializer(envelope => {
        if (!envelope.data) envelope.data = {};
        for (const [name, value] of Object.entries(context)) {
          envelope.data[name] = value;
        }
      });
    }
    window.appInsights = appInsights;

    // There are some exceptions that are "valid" and we don't want to track them
    // This is because it takes up app insights quota and we want "exception" to be
    // a signal that something is wrong
    const hasExceptionRegex = /exception/i;
    // If the exception message matches any of these regexes, we don't track it
    const FilteredExceptions: RegExp[] = [/ResizeObserver/];
    appInsights.addTelemetryInitializer(envelope => {
      if (hasExceptionRegex.test(envelope.name)) {
        log.debug(`Envelope is an exception`);
        if (FilteredExceptions.some(exception => envelope.data?.message && exception.test(envelope.data.message))) {
          log.debug(`Envelope is a filtered exception`);
          return false;
        }
        log.debug(`Did not filter the exception`);
        return true;
      }
    });
  }, [appInsights, log]);

  useEffect(() => {
    logger.settings.attachedTransports = [
      log => {
        if (!appInsights.appInsights.isInitialized()) {
          return;
        }
        const message = log['0'] as unknown as string;
        const logLevel = log._meta.logLevelId;
        if (logLevel < level) return;
        const severityLevel = mapLogLevelToAppInsights(logLevel);
        appInsights.trackTrace({ message, severityLevel });
      }
    ];
  }, [appInsights, logger, level]);

  return <AppInsightsContext.Provider value={store}>{children}</AppInsightsContext.Provider>;
};

type SelectorReturn<S extends (s: AppInsightsStore) => any> = ReturnType<S>;

export function useAppInsights<S extends (s: AppInsightsStore) => any>(selector: S): SelectorReturn<S> {
  const context = useContext(AppInsightsContext);
  if (!context) {
    throw new Error('useAppInsights must be used within a AppInsightsProvider');
  }
  return useStore(context, selector);
}

export const useTrackEvent = () => useAppInsights(store => store.trackEvent);
export const useLogPage = (name: string, properties: Record<string, string>) => {
  const trackEvent = useTrackEvent();
  const ref = useRef<boolean>(false);
  useEffect(() => {
    if (ref.current) {
      trackEvent(`view:${name}`, properties);
    }
    ref.current = true;
  }, [trackEvent, name, properties]);
};
export const useAddAuthenticatedContext = () => {
  return useAppInsights(store => store.addAuthenticatedUserContext);
};

export const useAddCustomContext = () => useAppInsights(store => store.addCustomContext);

export const useSessionId = () => useAppInsights(store => store.getSessionId());
